import BrowserSourceMapsLoader from "./bug-reporting-source-maps.js"
import BugReporting from "tensorbuzz-api/build/bug-reporting/index.js"

const reporters = new WeakMap()

function safeText(value, limit = 1000) {
  if (typeof value !== "string") return ""

  return value
    .replace(/((?:https?:)?\/\/)[^\s/]*@/gi, "$1")
    .replace(/\b(?:data|blob|javascript):[^\r\n]*/gi, "[redacted]")
    // Drop the rest of the line, including quoted query values, retaining only a stack frame's coordinates.
    .replace(/[?#][^\r\n]*/g, (fragment) => fragment.match(/:\d+(?::\d+)?\)?$/)?.[0] || "")
    .slice(0, limit)
}

function safeUrl(value, base) {
  if (typeof value !== "string") return null

  try {
    const url = new URL(value, base)

    if (!["http:", "https:"].includes(url.protocol)) return null

    return `${url.origin}${url.pathname}`.slice(0, 500)
  } catch {
    return null
  }
}

function readProperty(value, key) {
  try {
    return value?.[key]
  } catch {
    return undefined
  }
}

function isError(value) {
  try {
    return value instanceof Error || Object.prototype.toString.call(value) === "[object Error]"
  } catch {
    return false
  }
}

function errorDetails(value, fallback) {
  if (isError(value)) {
    const name = readProperty(value, "name")

    return {
      name: typeof name === "string" && /^[\w.]{1,80}$/.test(name) ? name : "Error",
      message: safeText(readProperty(value, "message")) || fallback,
      stack: safeText(readProperty(value, "stack"), 8000)
    }
  }
  const primitive = value == null || ["string", "number", "boolean", "bigint"].includes(typeof value)

  return {
    name: "UnhandledRejection",
    message: primitive ? safeText(String(value)) : `${fallback} (${typeof value})`,
    stack: ""
  }
}

export default function initializeBugReporting(browser) {
  if (reporters.has(browser)) return reporters.get(browser)

  const configuration = browser.tensorBuzzBugReporting
  const applicationEnvironment = configuration?.applicationEnvironment

  if (!["production", "staging"].includes(applicationEnvironment) || ["development", "test"].includes(browser.RAILS_ENV)) return
  if (typeof configuration.authToken !== "string" || !configuration.authToken.trim()) return
  if (typeof configuration.projectId !== "string" || !configuration.projectId.trim()) return
  if (typeof browser.fetch !== "function" || typeof browser.AbortController !== "function") return

  const postUrl = configuration.postUrl || undefined

  if (postUrl) {
    try {
      const url = new URL(postUrl)

      if (url.protocol !== "https:" || url.href !== safeUrl(url.href)) return
    } catch {
      return
    }
  }

  class BrowserRequest {
    async send(url, data) {
      const controller = new browser.AbortController()
      let timer

      // Allowlist the wire payload even when the SDK adds fields or an Error carries custom metadata.
      const payload = {
        auth_token: data.auth_token,
        projectId: data.projectId,
        hostname: browser.location.hostname,
        runtimeEnvironment: "browser",
        error: {
          error_class: data.error.error_class,
          message: safeText(data.error.message),
          backtrace: data.error.backtrace?.slice(0, 40).map((frame) => safeText(frame, 8000)),
          url: safeUrl(data.error.url, browser.location.href),
          user_agent: safeText(data.error.user_agent, 300),
          environment: {...data.error.environment, ...environment},
          parameters: {}
        }
      }

      try {
        return await Promise.race([
          browser.fetch(url, {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify(payload),
            credentials: "omit",
            referrerPolicy: "no-referrer",
            redirect: "error",
            signal: controller.signal
          }).then(async (response) => {
            if (!response.ok) throw new Error("Bug report unavailable")

            return await response.text()
          }),
          new Promise((_resolve, reject) => {
            timer = browser.setTimeout(() => {
              controller.abort()
              reject(new Error("Bug report timed out"))
            }, 5000)
          })
        ])
      } finally {
        browser.clearTimeout(timer)
      }
    }
  }

  const sdk = new BugReporting({
    authToken: configuration.authToken,
    projectId: configuration.projectId,
    hostname: browser.location.hostname,
    postUrl,
    RequestClass: BrowserRequest,
    runtimeEnvironment: "browser"
  })
  const environment = {
    application_environment: applicationEnvironment,
    release: safeText(configuration.release, 100)
  }
  const seen = new WeakSet()
  const breadcrumbs = []
  const listeners = []
  let lastUrl
  let observer

  sdk.collectParams(() => ({}))
  sdk.collectEnvironment(() => environment)
  sdk.sourceMapsLoader = new BrowserSourceMapsLoader(browser)

  const breadcrumb = (data) => {
    breadcrumbs.push({timestamp: Date.now(), ...data})
    if (breadcrumbs.length > 20) breadcrumbs.shift()
  }
  const navigation = () => {
    const url = safeUrl(browser.location.href)

    if (url !== lastUrl) {
      breadcrumb({category: "navigation", url})
      lastUrl = url
    }
  }
  const listen = (target, name, callback) => {
    target.addEventListener(name, callback)
    listeners.push(() => target.removeEventListener(name, callback))
  }

  async function report(value, event = {}) {
    let identity

    try {
      identity = isError(value) ? value : undefined
      if (identity && seen.has(identity)) return false
      if (identity) seen.add(identity)
      navigation()
      const details = errorDetails(value, "Unhandled promise rejection")
      const error = new Error(safeText(event.message) || details.message)
      const causes = []
      const visited = new Set([value])
      let cause = readProperty(identity, "cause")

      error.name = event.errorClass || details.name
      error.stack = details.stack
      if (!error.stack && event.file) {
        error.stack = `${safeUrl(event.file, browser.location.href)}:${Number(event.line) || 0}:${Number(event.column) || 0}`
      }
      while (cause !== undefined && !visited.has(cause) && causes.length < 3) {
        visited.add(cause)
        const diagnostic = errorDetails(cause, "Non-Error cause")

        causes.push({...diagnostic, stack: diagnostic.stack.slice(0, 1000)})
        cause = readProperty(cause, "cause")
      }
      error.tensorbuzzEnvironment = {...environment, breadcrumbs: breadcrumbs.slice(), causes}
      await sdk.handleError({error, errorClass: error.name, message: error.message, url: safeUrl(browser.location.href)})

      return true
    } catch {
      // Never turn a failed report into another global rejection; allow later reporting to recover.
      if (identity) seen.delete(identity)

      return false
    }
  }

  // Do not call connect(): the SDK's global listeners drop concurrent errors and can reject recursively.
  listen(browser, "error", (event) => {
    void report(event.error, {
      errorClass: isError(event.error) ? undefined : "Error",
      message: event.message || (isError(event.error) ? undefined : "Unknown browser error"),
      file: event.filename,
      line: event.lineno,
      column: event.colno
    })
  })
  listen(browser, "unhandledrejection", (event) => {
    void report(event.reason)
  })
  listen(browser, "popstate", navigation)
  listen(browser, "hashchange", navigation)
  listen(browser.document, "click", (event) => {
    navigation()
    const tag = readProperty(event.target, "tagName")

    if (["A", "BUTTON", "INPUT", "SELECT", "TEXTAREA"].includes(tag)) breadcrumb({category: "click", tag})
  })

  // Resource timing observes fetch and XHR without wrapping application transports or reading their bodies.
  try {
    observer = new browser.PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!["fetch", "xmlhttprequest"].includes(entry.initiatorType)) continue
        const url = safeUrl(entry.name)

        if (!url || url === safeUrl(sdk.postUrl) || url.endsWith(".map")) continue
        breadcrumb({category: "network", origin: new URL(url).origin, duration_ms: Math.min(60000, Math.max(0, entry.duration))})
      }
    })
    observer.observe({type: "resource"})
  } catch {
    // Resource timing is optional, including in browsers that do not support this entry type.
  }
  navigation()
  const reporter = {
    report,
    disconnect() {
      for (const remove of listeners) remove()
      observer?.disconnect()
      reporters.delete(browser)
    }
  }

  reporters.set(browser, reporter)

  return reporter
}
